import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useRoleTypes } from "@/lib/roles";
import { pluralCircles, pluralEvents, pluralInvited } from "@/lib/plural";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/admin/roles")({
  head: () => ({
    meta: [
      { title: "Роли и титулы — админка TurkeyDrive" },
      { name: "description", content: "Сводка ролей и титулов резидентов TurkeyDrive." },
      { property: "og:title", content: "Роли и титулы — админка TurkeyDrive" },
      { property: "og:description", content: "Сводка ролей и титулов резидентов TurkeyDrive." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminRolesPage,
});

type CountRow = {
  role_key: string;
  title: string;
  display_title: string;
  kind: string;
  origin: string;
  icon: string | null;
  sort_order: number;
  project_id: string | null;
  project_title: string | null;
  holders: number;
};
type SelectedRole = { roleKey: string; projectId: string | null };
type HolderRow = {
  user_id: string;
  full_name: string | null;
  username: string | null;
  photo_url: string | null;
  city: string | null;
  counter_value: number | null;
  project_title: string | null;
  granted_at: string | null;
  note: string | null;
  resident_role_id: string | null;
};

const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString("ru-RU") : "");

function counterLabel(key: string, n: number): string {
  if (key === "organizer") return pluralEvents(n);
  if (key === "ambassador") return pluralInvited(n);
  if (key === "circle_host") return pluralCircles(n);
  return String(n);
}

function AdminRolesPage() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [selected, setSelected] = useState<SelectedRole | null>(null);

  useEffect(() => {
    (async () => {
      const { data: userRes } = await supabase.auth.getUser();
      const uid = userRes.user?.id;
      if (!uid) return navigate({ to: "/home" });
      const { data: prof } = await supabase.from("profiles").select("is_admin").eq("user_id", uid).maybeSingle();
      if (!(prof as { is_admin?: boolean } | null)?.is_admin) return navigate({ to: "/home" });
      setAuthorized(true);
    })();
  }, [navigate]);

  const { data: counts = [], isLoading } = useQuery({
    queryKey: ["admin", "role-counts"],
    enabled: authorized === true,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("role_counts");
      if (error) throw error;
      return (data ?? []) as CountRow[];
    },
  });

  if (authorized !== true) {
    return <div className="mx-auto max-w-5xl px-4 py-8 text-sm text-td-muted">Проверка доступа…</div>;
  }

  const selectedRow =
    counts.find((c) => c.role_key === selected?.roleKey && (c.project_id ?? null) === (selected?.projectId ?? null)) ?? null;

  return (
    <main className="mx-auto max-w-3xl px-4 py-8">
      <button type="button" onClick={() => navigate({ to: "/profile" })} className="mb-4 text-sm text-td-muted hover:text-td-dark">
        ← Назад
      </button>
      <h1 className="mb-4 text-2xl font-semibold text-td-dark">Роли и титулы</h1>

      {isLoading ? (
        <div className="text-sm text-td-muted">Загружаем…</div>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {counts.map((c) => {
            const isSel = selected?.roleKey === c.role_key && (selected?.projectId ?? null) === (c.project_id ?? null);
            return (
              <button
                key={`${c.role_key}:${c.project_id ?? ""}`}
                type="button"
                onClick={() => setSelected({ roleKey: c.role_key, projectId: c.project_id ?? null })}
                className={`flex min-w-0 flex-col rounded-2xl border p-3 text-left transition ${
                  isSel ? "border-td-dark bg-td-soft/60" : "border-td-line bg-card hover:bg-td-soft/40"
                }`}
              >
                <div className="text-xs text-td-muted">{c.kind === "title" ? "Титул" : "Роль"}</div>
                <div className="break-words text-sm font-medium text-td-dark">
                  {c.icon} {c.display_title}
                </div>
                <div className="mt-auto pt-1 text-2xl font-semibold text-td-dark">{c.holders}</div>
              </button>
            );
          })}
        </div>
      )}

      {selectedRow ? <HoldersPanel role={selectedRow} /> : null}
    </main>
  );
}

function HoldersPanel({ role }: { role: CountRow }) {
  const qc = useQueryClient();
  const { data: types = [] } = useRoleTypes();
  const type = types.find((t) => t.key === role.role_key);
  const [grantOpen, setGrantOpen] = useState(false);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [hideId, setHideId] = useState<string | null>(null);
  const [hideReason, setHideReason] = useState("");
  const isExpertRole = role.role_key === "expert";

  async function setExpertHidden(userId: string, hidden: boolean, why: string | null) {
    const { error } = await supabase.rpc("admin_set_expert_hidden", { p_user_id: userId, p_hidden: hidden, p_reason: why ?? undefined });
    if (error) { toast.error(error.message); return; }
    toast.success(hidden ? "Скрыт из экспертов" : "Роль эксперта возвращена");
    setHideId(null);
    setHideReason("");
    refresh();
    qc.invalidateQueries({ queryKey: ["people"] });
  }

  const { data: holders = [], isLoading } = useQuery({
    queryKey: ["admin", "role-holders", role.role_key, role.project_id ?? null],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("role_holders", {
        p_role_key: role.role_key,
        p_project_id: role.project_id ?? undefined,
      });
      if (error) throw error;
      return (data ?? []) as HolderRow[];
    },
  });

  function refresh() {
    qc.invalidateQueries({ queryKey: ["admin", "role-counts"] });
    qc.invalidateQueries({ queryKey: ["admin", "role-holders"] });
    qc.invalidateQueries({ queryKey: ["resident-roles"] });
  }

  async function copyList() {
    const text = holders.map((h) => `${h.full_name ?? "—"}${h.username ? ` (@${h.username})` : ""}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Список скопирован");
    } catch {
      toast.error("Не удалось скопировать");
    }
  }

  async function revoke() {
    if (!revokeId) return;
    const { error } = await supabase
      .from("resident_roles")
      .update({ revoked_at: new Date().toISOString(), revoke_reason: reason.trim() || null })
      .eq("id", revokeId);
    if (error) return toast.error(error.message);
    toast.success("Снято");
    setRevokeId(null);
    setReason("");
    refresh();
  }

  return (
    <section className="mt-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-td-dark">
          {role.icon} {role.display_title}
        </h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={copyList} disabled={holders.length === 0}>
            Скопировать список
          </Button>
          {role.origin === "granted" ? (
            <Button size="sm" onClick={() => setGrantOpen(true)}>
              Выдать
            </Button>
          ) : null}
        </div>
      </div>
      {role.origin === "auto" ? (
        <div className="mb-3 rounded-xl bg-td-soft/40 p-3 text-sm text-td-dark">
          <div className="font-medium">Начисляется автоматически</div>
          {type?.description ? <div className="mt-1 text-td-muted">{type.description}</div> : null}
        </div>
      ) : null}

      {isLoading ? (
        <div className="text-sm text-td-muted">Загружаем…</div>
      ) : holders.length === 0 ? (
        <div className="text-sm text-td-muted">Пока никто не носит.</div>
      ) : (
        <ul className="divide-y divide-td-line overflow-hidden rounded-2xl border border-td-line bg-card">
          {holders.map((h, i) => (
            <li key={h.resident_role_id ?? `${h.user_id}-${i}`} className="flex items-start gap-3 px-3 py-2">
              {h.photo_url ? (
                <img loading="lazy" decoding="async" src={h.photo_url} alt="" className="size-9 shrink-0 rounded-full object-cover" />
              ) : (
                <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-td-soft text-sm text-td-dark">
                  {(h.full_name ?? "?").charAt(0)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <Link to="/people/$userId" params={{ userId: h.user_id }} className="block truncate text-sm font-medium text-td-dark hover:underline">
                  {h.full_name ?? "—"}
                </Link>
                {h.city ? <div className="truncate text-xs text-td-muted">{h.city}</div> : null}
                {isExpertRole && h.project_title ? <div className="truncate text-xs text-td-teal">{h.project_title}</div> : null}
                {isExpertRole && h.note ? <div className="mt-1 break-words text-xs text-amber-700">{h.note}</div> : null}
                {h.resident_role_id && h.note ? <div className="mt-1 break-words text-xs text-td-dark/80">{h.note}</div> : null}
              </div>
              <div className="shrink-0 text-right text-xs text-td-muted">
                {h.resident_role_id ? (
                  <>
                    {h.project_title ? <div className="text-td-dark">«{h.project_title}»</div> : null}
                    {h.granted_at ? <div>{fmt(h.granted_at)}</div> : null}
                    <Button size="sm" variant="outline" className="mt-1" onClick={() => setRevokeId(h.resident_role_id)}>
                      Снять
                    </Button>
                  </>
                ) : isExpertRole ? (
                  <div className="flex flex-col items-end gap-1">
                    <Button size="sm" variant="outline" onClick={() => { setHideId(h.user_id); setHideReason(""); }}>Скрыть из экспертов</Button>
                    <Button size="sm" variant="ghost" onClick={() => setExpertHidden(h.user_id, false, null)}>Вернуть</Button>
                  </div>
                ) : (
                  <span className="text-sm text-td-dark">{counterLabel(role.role_key, h.counter_value ?? 0)}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {grantOpen && type ? (
        <GrantDialog
          roleKey={role.role_key}
          title={role.display_title}
          scope={type.scope}
          projectId={role.project_id ?? null}
          projectTitle={role.project_title ?? null}
          open={grantOpen}
          onOpenChange={setGrantOpen}
          onDone={refresh}
        />
      ) : null}

      <AlertDialog open={!!hideId} onOpenChange={(v) => !v && setHideId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Скрыть из экспертов?</AlertDialogTitle>
            <AlertDialogDescription>Роль «Эксперт» пропадёт, пользователь не сможет вернуть её сам.</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea placeholder="Причина (её увидит пользователь)" value={hideReason} onChange={(e) => setHideReason(e.target.value)} rows={2} />
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={() => hideId && setExpertHidden(hideId, true, hideReason.trim() || null)}>Скрыть</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!revokeId} onOpenChange={(v) => !v && setRevokeId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Снять «{role.display_title}»?</AlertDialogTitle>
            <AlertDialogDescription>Запись останется в истории резидента с датой снятия.</AlertDialogDescription>
          </AlertDialogHeader>
          <Input placeholder="Причина (необязательно)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={revoke}>Снять</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function GrantDialog({
  roleKey,
  title,
  scope,
  projectId: fixedProjectId,
  projectTitle,
  open,
  onOpenChange,
  onDone,
}: {
  roleKey: string;
  title: string;
  scope: string;
  projectId: string | null;
  projectTitle: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onDone: () => void;
}) {
  const [q, setQ] = useState("");
  const [person, setPerson] = useState<{ user_id: string; full_name: string; username: string } | null>(null);
  const [projectId, setProjectId] = useState(fixedProjectId ?? "");
  const [eventId, setEventId] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const needsProject = scope === "project";
  const needsEvent = scope === "event";

  const term = q.trim().replace(/[,()%]/g, "");
  const { data: suggestions = [] } = useQuery({
    queryKey: ["admin", "profile-search", term],
    enabled: term.length >= 2 && !person,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, full_name, username, city")
        .or(`full_name.ilike.%${term}%,username.ilike.%${term}%`)
        .order("full_name")
        .limit(10);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["projects-active"],
    enabled: needsProject,
    queryFn: async () => {
      const { data, error } = await supabase.from("projects").select("id, title").eq("is_active", true).order("sort_order");
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: events = [] } = useQuery({
    queryKey: ["admin-published-events"],
    enabled: needsEvent,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, title, event_date")
        .eq("status", "published")
        .order("event_date", { ascending: false })
        .limit(300);
      if (error) throw error;
      return data ?? [];
    },
  });

  const canSave = useMemo(
    () => !!person && (!needsProject || !!projectId) && (!needsEvent || !!eventId) && !saving,
    [person, needsProject, projectId, needsEvent, eventId, saving],
  );

  async function save() {
    if (!person) return;
    setSaving(true);
    try {
      const { data: me } = await supabase.auth.getUser();
      const { error } = await supabase.from("resident_roles").insert({
        user_id: person.user_id,
        role_key: roleKey,
        project_id: needsProject ? projectId : null,
        event_id: needsEvent ? eventId : null,
        note: note.trim() || null,
        granted_by: me.user?.id ?? null,
      });
      if (error) throw error;
      toast.success("Выдано");
      onDone();
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось выдать");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Выдать «{title}»</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          {person ? (
            <div className="flex items-center justify-between gap-2 rounded-xl border border-td-line px-3 py-2 text-sm">
              <span className="min-w-0 truncate">
                {person.full_name} <span className="text-td-muted">@{person.username}</span>
              </span>
              <Button size="sm" variant="ghost" onClick={() => setPerson(null)}>
                Изменить
              </Button>
            </div>
          ) : (
            <>
              <Input placeholder="Имя или username" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
              {suggestions.length > 0 ? (
                <ul className="max-h-60 overflow-y-auto rounded-xl border border-td-line">
                  {suggestions.map((s) => (
                    <li key={s.user_id}>
                      <button
                        type="button"
                        onClick={() => setPerson(s)}
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-td-soft/50"
                      >
                        <span className="text-td-dark">{s.full_name}</span>{" "}
                        <span className="text-xs text-td-muted">@{s.username} · {s.city}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          )}
          {needsProject && fixedProjectId ? (
            <div className="rounded-xl border border-td-line px-3 py-2 text-sm text-td-dark">
              Движение: «{projectTitle ?? "—"}»
            </div>
          ) : null}
          {needsProject && !fixedProjectId ? (
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger><SelectValue placeholder="Движение (обязательно)" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : null}
          {needsEvent ? (
            <Select value={eventId} onValueChange={setEventId}>
              <SelectTrigger><SelectValue placeholder="Встреча (обязательно)" /></SelectTrigger>
              <SelectContent>
                {events.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.title}{e.event_date ? ` · ${fmt(e.event_date)}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          <Textarea placeholder="Комментарий (необязательно)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          <Button className="w-full" onClick={save} disabled={!canSave}>
            {saving ? "Сохраняем…" : "Выдать"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
